
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const PrintQuoteScreen = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { message, chatInfo } = location.state || {};

    useEffect(() => {
        if (message && chatInfo) {
            const timer = setTimeout(() => window.print(), 500);
            const afterPrint = () => navigate(-1);
            window.addEventListener('afterprint', afterPrint);

            return () => {
                clearTimeout(timer);
                window.removeEventListener('afterprint', afterPrint);
            };
        } else {
            navigate('/');
        }
    }, [message, chatInfo, navigate]);

    if (!message || !chatInfo) {
        return <p>Carregando documento...</p>;
    }

    const { quote } = message;

    // Status do documento
    const statusDisplay = {
        pending: { text: 'Aguardando Aprovação', className: 'bg-yellow-400 text-yellow-900' },
        accepted: { text: 'Serviço Aprovado', className: 'bg-green-500 text-white' },
        rejected: { text: 'Serviço Recusado', className: 'bg-red-500 text-white' },
        Pago: { text: 'Serviço Pago', className: 'bg-green-600 text-white' },
        Finalizado: { text: 'Serviço Finalizado', className: 'bg-gray-600 text-white' },
    };
    const currentStatus = statusDisplay[quote.status] || statusDisplay[chatInfo.status] || { text: quote.status || chatInfo.status, className: 'bg-gray-400' };

    // BUSCA EXTREMAMENTE RESILIENTE PELO NOME DA EMPRESA
    // Pesquisa em todas as camadas de dados que o cliente recebeu
    const getFinalDisplayName = () => {
        // 1. Tenta no objeto serviDetails que enviamos (prioridade máxima)
        if (quote.serviDetails?.companyName) return quote.serviDetails.companyName;

        // 2. Tenta nos detalhes do chat
        if (chatInfo.participantDetails?.[chatInfo.serviId]?.companyName) {
            return chatInfo.participantDetails[chatInfo.serviId].companyName;
        }

        // 3. Tenta garimpar nas propostas incluídas
        if (quote.includedProposals) {
            const found = quote.includedProposals.find(p => p.serviDetails?.companyName);
            if (found) return found.serviDetails.companyName;
        }

        // 4. Fallback final: Nome do profissional (Thiago...)
        return chatInfo.participantDetails?.[chatInfo.serviId]?.name || quote.serviDetails?.name || 'Prestador';
    };

    const displayName = getFinalDisplayName();

    return (
        <div className="p-6 pt-12 font-sans flex flex-col justify-between h-screen bg-white">
            <div>
                <header className="flex justify-between items-start mb-4 pb-2 border-b-2 border-black">
                    <div>
                        <h1 className="text-xl font-bold uppercase tracking-tight">Resumo de Atendimento</h1>
                        <p className="text-gray-600 text-xs font-medium">{chatInfo.serviceTitle}</p>
                    </div>
                    <div className="text-right">
                        <p className="font-black text-lg text-gray-900 leading-tight">{displayName}</p>
                        {/* INFORMAÇÃO "PROFISSIONAL: ..." REMOVIDA DEFINITIVAMENTE */}
                        <div className={`mt-1 text-[9px] font-black px-2 py-0.5 rounded border border-current uppercase ${currentStatus.className}`}>
                            {currentStatus.text}
                        </div>
                    </div>
                </header>

                <main>
                    <div className="grid grid-cols-2 gap-4 mb-4 text-xs">
                        <div>
                            <h2 className="text-[9px] font-black text-gray-400 uppercase mb-0.5">Cliente:</h2>
                            <p className="font-bold text-gray-800">{chatInfo.participantDetails?.[chatInfo.clientId]?.name || 'Cliente'}</p>
                        </div>
                        <div className="text-right">
                            <h2 className="text-[9px] font-black text-gray-400 uppercase mb-0.5">Data:</h2>
                            <p className="font-bold text-gray-800">{new Date((message.timestamp?.seconds || Date.now()/1000) * 1000).toLocaleDateString('pt-BR')}</p>
                        </div>
                    </div>

                    <table className="w-full text-left border-collapse mb-4">
                        <thead>
                            <tr className="border-b-2 border-gray-800">
                                <th className="py-1 text-[9px] font-black text-gray-500 uppercase">Descrição do Serviço / Item</th>
                                <th className="py-1 text-[9px] font-black text-gray-500 uppercase text-right">Valor Unitário</th>
                            </tr>
                        </thead>
                        <tbody className="text-xs">
                            {(quote.items || []).map((item, index) => (
                                <tr key={index} className="border-b border-gray-100">
                                    <td className="py-1.5 text-gray-700 font-medium">{item.description}</td>
                                    <td className="py-1.5 text-right font-bold text-gray-900 font-mono">
                                        R$ {(parseFloat(item.price) || 0).toFixed(2).replace('.', ',')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td className="pt-4 pb-1 text-right font-black text-gray-500 uppercase text-[10px] tracking-widest">Valor Total do Serviço</td>
                                <td className="pt-4 pb-1 text-right font-black text-lg text-gray-900 font-mono">
                                    R$ {(parseFloat(quote.total) || 0).toFixed(2).replace('.', ',')}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </main>
            </div>

            <footer className="text-center text-[8px] text-gray-400 mt-4 pt-2 border-t border-gray-100 italic">
                Este documento é um comprovante informativo gerado pelo sistema T-SERVI e não substitui a Nota Fiscal de Serviços (NFS-e).
            </footer>
        </div>
    );
};

export default PrintQuoteScreen;
